import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FiEdit } from 'react-icons/fi';

import TmFilter from '../ReusableComponents/TmFilter';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import Loader from '../ReusableComponents/Loader';
import SearchInput from '../ReusableComponents/SearchInput';

import { MONTHS } from '../../constant/constant';
import { generateYears } from '../../ReusableFn/reusableFn';
import style from '../../Styles/EmployeesReview.module.css';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import { toastError } from '../Toast/ToastComponent';
import { useSelector } from 'react-redux';
import { getEmployeesReview } from '../../store/reducers/actionPermission';
import { Flex } from 'antd';
import { useAuth } from '../../Context/AuthContext';

/**
 * Status -> CSS module class for the colored pill
 */
const STATUS_CLASS_MAP = {
  completed: style.statusCompleted,
  draft: style.statusDraft,
  'hr review': style.statusHrReview,
  'manager review': style.statusManagerReview,
  pending: style.statusPending,
  'not started': style.statusDraft,
};

const STATUS_OPTIONS = [
  { label: 'All Status', value: 'all' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Draft', value: 'Draft' },
  { label: 'HR Review', value: 'HR Review' },
  { label: 'Manager Review', value: 'Manager Review' },
  // { label: 'Pending', value: 'Pending' },
  { label: 'Not Started', value: 'Not Started' },
];

const DEPARTMENT_OPTIONS = [{ label: 'All Departments', value: 'all' }];

const StatusPill = ({ status }) => {
  if (!status) return <span>--</span>;
  const cls = STATUS_CLASS_MAP[String(status).toLowerCase()] || '';
  return <span className={`${style.statusPill} ${cls}`}>{status}</span>;
};

/**
 * Transform API response to table row format
 */
function mapReviewToRow(review) {
  const statusLabel = review.review_status || '--';
  const userName = review.user?.first_name + ' ' + review.user?.last_name;

  return {
    id: review.id,
    user_id: review.user.id,
    employee_name: userName,
    role: review.designation_name || '--',
    department: review.department_name || '--',
    reporting_manager: review.department_head_names?.length
      ? review.department_head_names
      : '--',
    review_month: review.month ? `${review.month} ${review.year}` : '--',
    status: statusLabel,
    month: review.month,
    year: review.year,
  };
}

const EmployeesListComp = () => {
  const navigate = useNavigate();
  const permission = useSelector(getEmployeesReview);
  const { authState } = useAuth();
  const debounceTimeoutRef = useRef(null);

  const currentMonthLabel = useMemo(() => dayjs().format('MMMM'), []);
  const currentYear = useMemo(() => dayjs().year(), []);

  const [filterArr, setFilterArr] = useState({
    month: currentMonthLabel,
    year: currentYear,
    department: 'all',
    status: 'all',
    search: '',
  });
  const filterArrRef = useRef(filterArr);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    filterArrRef.current = filterArr;
  }, [filterArr]);

  const buttonPermission = useMemo(() => {
    return {
      view: permission?.actions?.find((action) => action.statename === 'View')
        ?.checked,
      hrReview: permission?.actions?.find(
        (action) => action.statename === 'HrReview',
      )?.checked,
      managerReview: permission?.actions?.find(
        (action) => action.statename === 'ManagerReview',
      )?.checked,
    };
  }, [permission]);

  const yearOptions = useMemo(() => generateYears() || [], []);

  const filterArray = useMemo(
    () => [
      {
        options: MONTHS,
        placeholder: 'Month',
        controlName: 'month',
        input: 'select',
        defaultValue: currentMonthLabel,
        col: 2,
        size: 'small',
        allowClear: false,
      },
      {
        options: yearOptions,
        placeholder: 'Year',
        controlName: 'year',
        input: 'select',
        defaultValue: currentYear,
        col: 2,
        size: 'small',
        allowClear: false,
      },
      {
        options: DEPARTMENT_OPTIONS,
        placeholder: 'All Departments',
        controlName: 'department',
        input: 'select',
        defaultValue: 'all',
        col: 2,
        size: 'small',
        allowClear: true,
      },
      {
        options: STATUS_OPTIONS,
        placeholder: 'All Status',
        controlName: 'status',
        input: 'select',
        defaultValue: 'all',
        col: 2,
        size: 'small',
        allowClear: true,
      },
    ],
    [yearOptions, currentMonthLabel, currentYear],
  );

  const handlePermission = useCallback(
    (status, previousStatus) => {
      let canAccess = false;

      if (
        status === 'Hr Review' ||
        (status === 'Draft' && previousStatus === 'Hr Review')
      ) {
        canAccess = buttonPermission.hrReview;
      } else if (
        status === 'Manager Review' ||
        (status === 'Draft' && previousStatus === 'Manager Review')
      ) {
        canAccess = buttonPermission.managerReview;
      } else if (status === 'Draft' || status === 'Completed') {
        canAccess = buttonPermission.hrReview || buttonPermission.managerReview;
      } else if (status === 'Not Started') {
        canAccess = buttonPermission.managerReview;
      } else {
        canAccess = false;
      }

      return canAccess;
    },
    [buttonPermission.hrReview, buttonPermission.managerReview],
  );

  const loadReviews = useCallback(async (filters) => {
    if (!authState?.id) return;
    try {
      setLoading(true);
      const payload = {
        month: filters.month,
        year: filters.year,
        status: filters.status === 'all' ? '' : (filters.status || ''),
        departmentID: filters.department === 'all' ? '' : (filters.department || ''),
        search: filters.search || '',
        user_id: authState?.id,
        userRoleID: authState?.role_id || authState?.userRoleID || authState?.roleId || '',
      };
      const response = await EmployeesReviewApi.getAllUserReviews(payload);
      const result = response?.data?.result || [];
      const rows = result.map((review, index) => ({ ...mapReviewToRow(review), serialNo: index + 1 }));
      setData(rows);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toastError(error?.response?.data?.message || 'Failed to load reviews.');
      setData([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    loadReviews(filterArr);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterArr.month, filterArr.year, authState]);

  const handleFilterChange = (value) => {
    const merged = { ...filterArrRef.current, ...value };
    setFilterArr(merged);
    filterArrRef.current = merged;
    loadReviews(merged);
  };

  const handleSearchChange = (value) => {
    const merged = { ...filterArrRef.current, search: value };
    setFilterArr(merged);
    filterArrRef.current = merged;

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      loadReviews(merged);
    }, 400);
  };

  const handleReview = useCallback((row) => {
    if (row.status === 'Not Started') {
      navigate(
        `/employees/new-review?month=${filterArrRef.current.month}&year=${filterArrRef.current.year}&user_id=${row.user_id}`,
      );
      return;
    }
    navigate(`/employees/${row.id}`);
  }, []);

  const columns = useMemo(
    () => [
        { field: 'serialNo', headerName: 'S.NO', className: '' },
      {
        field: 'employee_name',
        headerName: 'Employee Name',
        render: (val) => <span title={val}>{val ?? '--'}</span>,
      },
      // {
      //   field: 'role',
      //   headerName: 'Designation',
      //   render: (val) => <span title={val}>{val ?? '--'}</span>,
      // },
      {
        field: 'department',
        headerName: 'Department',
        render: (val) => <span title={val}>{val ?? '--'}</span>,
      },
      {
        field: 'reporting_manager',
        headerName: 'Reporting Manager',
        render: (val) =>
          val && val !== '--' && Array.isArray(val) ? (
            <Flex gap={8}>
              {val.map((manager) => (
                <span key={manager.id} className={`${style.managerTag}`}>
                  {`${manager.first_name} ${manager.last_name}`}
                </span>
              ))}
            </Flex>
          ) : (
            <span>--</span>
          ),
      },
      {
        field: 'review_month',
        headerName: 'Review Month',
        render: (val) => <span>{val ?? '--'}</span>,
      },
      {
        field: 'status',
        headerName: 'Status',
        render: (val) => <StatusPill status={val} />,
      },
      {
        field: 'action',
        headerName: 'Action',
        className: 'text-center',
        render: (_, row) =>
          handlePermission(row.status, row.previous_status) ? (
            <button
              type="button"
              className={style.reviewBtn}
              onClick={() => handleReview(row)}
              title="Review"
            >
              <FiEdit size={14} />
              <span>Review</span>
            </button>
          ) : (
            <span>--</span>
          ),
      },
    ],
    [handlePermission, handleReview],
  );

  return (
    <>
      <div className="d-md-flex align-items-center mb-2 flex-wrap gap-2">
        <h5 className="mb-0 primaryColor">Employees Review</h5>
        <div className="ms-auto d-flex align-items-center">
          <div className="me-2">
            <SearchInput
              placeholder="Search"
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-2 mb-2">
        <TmFilter
          filterArray={filterArray}
          handleFilterChange={handleFilterChange}
          clearDatesOnReset
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <TmMasterTable rows={data} columns={columns} custom={[]} />
      )}
    </>
  );
};

export default EmployeesListComp;
 